import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: "",
  category: "tool",
  size: "md",
  slug: "",
  icon_url: "",
  initials: "",
  color: "0A0A0A",
  order: 0,
};

function resolveIcon(item) {
  if (item.icon_url) return item.icon_url.startsWith("/") ? `${BACKEND}${item.icon_url}` : item.icon_url;
  if (item.slug) return `https://cdn.simpleicons.org/${item.slug}/${item.color || "000"}`;
  return null;
}

export default function ExperienceAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/experience").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Icon larger than 10MB.");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/uploads", data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, icon_url: r.data.url }));
      toast.success("Icon uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      name: it.name || "",
      category: it.category || "tool",
      size: it.size || "md",
      slug: it.slug || "",
      icon_url: it.icon_url || "",
      initials: it.initials || "",
      color: it.color || "0A0A0A",
      order: it.order ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || null,
        icon_url: form.icon_url || null,
        initials: form.initials || null,
      };
      if (editingId) {
        await api.put(`/experience/${editingId}`, payload);
        toast.success("Item updated.");
      } else {
        await api.post("/experience", payload);
        toast.success("Item added.");
      }
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/experience/${id}`);
      toast.success("Deleted.");
      if (editingId === id) cancelEdit();
      await load();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const move = async (it, dir) => {
    const list = items.filter((i) => i.category === it.category).sort((a, b) => a.order - b.order);
    const idx = list.findIndex((x) => x.id === it.id);
    const swap = list[idx + dir];
    if (!swap) return;
    try {
      await Promise.all([
        api.put(`/experience/${it.id}`, { order: swap.order }),
        api.put(`/experience/${swap.id}`, { order: it.order }),
      ]);
      await load();
    } catch {
      toast.error("Reorder failed.");
    }
  };

  const tools = items.filter((i) => i.category === "tool").sort((a, b) => a.order - b.order);
  const code = items.filter((i) => i.category === "code").sort((a, b) => a.order - b.order);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="experience-admin">
      <section className="lg:col-span-7 space-y-10">
        {[
          { label: "Tools & Platforms", list: tools, showSize: true },
          { label: "Code", list: code, showSize: false },
        ].map((group) => (
          <div key={group.label}>
            <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-4">
              {group.label} ({group.list.length})
            </p>
            <div className="space-y-2">
              {group.list.map((it, idx) => {
                const src = resolveIcon(it);
                return (
                  <div
                    key={it.id}
                    className={`bg-white border p-3 flex gap-3 items-center ${editingId === it.id ? "border-[#FF3E00]" : "border-neutral-200"}`}
                    data-testid={`experience-row-${it.id}`}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 border border-neutral-100"
                      style={{ background: src ? "#fff" : `#${it.color}`, color: "#fff" }}
                    >
                      {src ? (
                        <img src={src} alt="" className="w-7 h-7 object-contain" />
                      ) : (
                        <span className="font-display font-black text-sm">{it.initials || it.name.slice(0, 2)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-base truncate">{it.name}</div>
                      <div className="font-body text-xs text-neutral-500">
                        order {it.order}
                        {group.showSize && ` · ${it.size}`}
                        {it.slug && ` · ${it.slug}`}
                        {it.icon_url && " · custom icon"}
                      </div>
                    </div>
                    <button onClick={() => move(it, -1)} disabled={idx === 0} className="font-body text-xs px-2 disabled:opacity-30" data-testid={`exp-up-${it.id}`}>↑</button>
                    <button onClick={() => move(it, 1)} disabled={idx === group.list.length - 1} className="font-body text-xs px-2 disabled:opacity-30" data-testid={`exp-down-${it.id}`}>↓</button>
                    <button onClick={() => startEdit(it)} className="font-body text-xs uppercase tracking-[0.2em] link-line" data-testid={`exp-edit-${it.id}`}>Edit</button>
                    <button onClick={() => remove(it.id)} className="font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line" data-testid={`exp-delete-${it.id}`}>Delete</button>
                  </div>
                );
              })}
              {group.list.length === 0 && <p className="font-body text-sm text-neutral-500">None yet.</p>}
            </div>
          </div>
        ))}
      </section>

      <aside className="lg:col-span-5">
        <div className="flex items-center justify-between mb-6">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">
            {editingId ? "Edit Item" : "New Item"}
          </p>
          {editingId && (
            <button onClick={cancelEdit} className="font-body text-xs uppercase tracking-[0.2em] link-line" data-testid="exp-cancel-edit">
              Cancel
            </button>
          )}
        </div>

        <form onSubmit={submit} className="space-y-1 bg-white border border-neutral-200 p-6" data-testid="experience-form">
          <input value={form.name} onChange={onChange("name")} placeholder="Name (e.g. WordPress)" className="input-bare" data-testid="exp-name-input" />

          <div className="pt-4 pb-2">
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Category</label>
            <div className="flex gap-3 mt-2">
              {["tool", "code"].map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setForm((f) => ({ ...f, category: c }))}
                  className={`px-4 py-2 border font-body text-xs uppercase tracking-[0.2em] ${form.category === c ? "bg-[#0A0A0A] text-[#F4F4F0] border-[#0A0A0A]" : "border-neutral-300"}`}
                  data-testid={`exp-cat-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {form.category === "tool" && (
            <div className="pb-2">
              <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Bubble size</label>
              <div className="flex gap-2 mt-2">
                {["xl", "lg", "md", "sm"].map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setForm((f) => ({ ...f, size: s }))}
                    className={`px-4 py-2 border font-body text-xs uppercase tracking-[0.2em] ${form.size === s ? "bg-[#FF3E00] text-white border-[#FF3E00]" : "border-neutral-300"}`}
                    data-testid={`exp-size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <input value={form.slug} onChange={onChange("slug")} placeholder="Simple Icons slug (optional, e.g. wordpress)" className="input-bare" data-testid="exp-slug-input" />
          <input value={form.initials} onChange={onChange("initials")} placeholder="Initials fallback (e.g. 365)" className="input-bare" data-testid="exp-initials-input" />
          <input value={form.color} onChange={onChange("color")} placeholder="Brand colour hex (no #), e.g. 0078D4" className="input-bare" data-testid="exp-color-input" />
          <input type="number" value={form.order} onChange={onChange("order")} placeholder="Order" className="input-bare" data-testid="exp-order-input" />

          <div className="pt-3 flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 font-body text-xs uppercase tracking-[0.2em] cursor-pointer hover:bg-neutral-50" data-testid="exp-upload-label">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                onChange={onIconUpload}
                className="hidden"
                disabled={uploading}
                data-testid="exp-icon-upload-input"
              />
              {uploading ? "Uploading…" : "↑ Upload custom icon"}
            </label>
            {form.icon_url && (
              <>
                <img src={form.icon_url.startsWith("/") ? `${BACKEND}${form.icon_url}` : form.icon_url} alt="preview" className="w-10 h-10 object-contain border border-neutral-200" data-testid="exp-icon-preview" />
                <button type="button" onClick={() => setForm((f) => ({ ...f, icon_url: "" }))} className="font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line">
                  Clear
                </button>
              </>
            )}
          </div>
          <p className="font-body text-[11px] text-neutral-500 pt-2 leading-relaxed">
            Priority: Custom icon &gt; Simple Icons slug &gt; Initials + colour.
          </p>

          <button
            type="submit"
            disabled={saving}
            className="mt-6 w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40"
            data-testid="exp-save-button"
          >
            {saving ? "Saving…" : editingId ? "Update Item" : "Add Item"}
          </button>
        </form>
      </aside>
    </div>
  );
}
